import { useEffect, useState } from 'react';
import Layout from '../components/Layout';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Checkbox } from '../components/ui/checkbox';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { 
  Plus, Edit, Trash2, Users, Monitor, Smartphone, Tablet, 
  LogOut, ChevronDown, ChevronUp, Shield, Lock
} from 'lucide-react';
import api from '../utils/api';
import { toast } from 'sonner';

// Main admin email (protected)
const MAIN_ADMIN_EMAIL = "muneeb.mahadik@lioncroft.co.uk";

// Timeout preset options (in seconds)
const TIMEOUT_PRESETS = [
  { label: '2 minutes', value: 120 },
  { label: '5 minutes', value: 300 },
  { label: '15 minutes', value: 900 },
  { label: '30 minutes', value: 1800 },
  { label: '1 hour', value: 3600 },
  { label: '4 hours', value: 14400 },
  { label: '8 hours', value: 28800 },
];

// Device icon based on type
const DeviceIcon = ({ type }) => {
  switch (type) {
    case 'mobile':
      return <Smartphone className="h-4 w-4 text-blue-500" />;
    case 'tablet':
      return <Tablet className="h-4 w-4 text-purple-500" />;
    default:
      return <Monitor className="h-4 w-4 text-gray-500" />;
  }
};

export default function UserManagement({ user, onLogout }) {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [expandedUser, setExpandedUser] = useState(null);
  const [userSessions, setUserSessions] = useState({});
  const [loadingSessions, setLoadingSessions] = useState({});
  const [availableModules, setAvailableModules] = useState([]);
  const [roleDefaults, setRoleDefaults] = useState({});
  const [formData, setFormData] = useState({
    full_name: '',
    email: '',
    password: '',
    role: 'Driver',
    session_timeout_seconds: 120,
    allowed_modules: null,
    module_permissions: {},
  });

  // Check if current user is main admin
  const isMainAdmin = user?.email === MAIN_ADMIN_EMAIL;
  const canEditModules = user?.role === 'Admin' || user?.role === 'Manager';

  useEffect(() => {
    fetchUsers();
    fetchModuleCatalog();
  }, []);

  const fetchModuleCatalog = async () => {
    try {
      const res = await api.get('/users/available-modules');
      setAvailableModules(res.data.modules || []);
      setRoleDefaults(res.data.role_defaults || {});
    } catch (error) {
      console.error('Failed to load module catalog:', error);
    }
  };

  const fetchUsers = async () => {
    try {
      const response = await api.get('/users');
      setUsers(response.data);
    } catch (error) {
      toast.error('Failed to load users');
    } finally {
      setLoading(false);
    }
  };

  const fetchUserSessions = async (userId) => {
    if (!isMainAdmin) return;
    
    setLoadingSessions(prev => ({ ...prev, [userId]: true }));
    try {
      const response = await api.get(`/sessions/user/${userId}`);
      setUserSessions(prev => ({ ...prev, [userId]: response.data }));
    } catch (error) {
      console.error('Failed to load sessions:', error);
      toast.error('Failed to load sessions');
    } finally {
      setLoadingSessions(prev => ({ ...prev, [userId]: false }));
    }
  };

  const handleForceLogout = async (sessionId, deviceName) => {
    if (!window.confirm(`Force logout this device: ${deviceName}?`)) return;
    
    try {
      await api.delete(`/sessions/${sessionId}`);
      toast.success(`Device "${deviceName}" logged out`);
      // Refresh sessions for all expanded users
      Object.keys(userSessions).forEach(userId => {
        if (userSessions[userId]?.length > 0) {
          fetchUserSessions(userId);
        }
      });
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to logout device');
    }
  };

  const toggleUserExpansion = (userId) => {
    if (expandedUser === userId) {
      setExpandedUser(null);
    } else {
      setExpandedUser(userId);
      if (!userSessions[userId]) {
        fetchUserSessions(userId);
      }
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const payload = {
        full_name: formData.full_name,
        email: formData.email,
        password: formData.password,
        role: formData.role,
        session_timeout_seconds: Number(formData.session_timeout_seconds) || 120,
      };
      if (canEditModules) {
        // Send explicit list so backend persists the selection (including empty = no access)
        payload.allowed_modules = Array.isArray(formData.allowed_modules)
          ? formData.allowed_modules
          : (roleDefaults[formData.role] || []);
        // Only include permissions for modules that are currently allowed
        const allowedSet = new Set(payload.allowed_modules);
        const cleanedPerms = {};
        Object.entries(formData.module_permissions || {}).forEach(([k, v]) => {
          if (allowedSet.has(k) && (v === 'view' || v === 'edit')) cleanedPerms[k] = v;
        });
        payload.module_permissions = cleanedPerms;
      }
      if (editingUser) {
        await api.put(`/users/${editingUser.id}`, payload);
        toast.success('User updated successfully');
      } else {
        await api.post('/users', payload);
        toast.success('User created successfully');
      }
      setDialogOpen(false);
      setEditingUser(null);
      setFormData({ full_name: '', email: '', password: '', role: 'Driver', session_timeout_seconds: 120, allowed_modules: null, module_permissions: {} });
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Operation failed');
    }
  };

  const handleEdit = (targetUser) => {
    // Check if trying to edit main admin as non-main-admin
    if (targetUser.email === MAIN_ADMIN_EMAIL && !isMainAdmin) {
      toast.error('Cannot edit the main administrator account');
      return;
    }
    
    setEditingUser(targetUser);
    // If user has no allowed_modules set, pre-fill from role default for transparency
    const effectiveModules = Array.isArray(targetUser.allowed_modules)
      ? targetUser.allowed_modules
      : (roleDefaults[targetUser.role] || []);
    setFormData({
      full_name: targetUser.full_name,
      email: targetUser.email,
      password: '', // Don't show existing password
      role: targetUser.role,
      session_timeout_seconds: targetUser.session_timeout_seconds || 120,
      allowed_modules: effectiveModules,
      module_permissions: (targetUser.module_permissions && typeof targetUser.module_permissions === 'object') ? { ...targetUser.module_permissions } : {},
    });
    setDialogOpen(true);
  };

  const handleDelete = async (id, email) => {
    // Check if trying to delete main admin
    if (email === MAIN_ADMIN_EMAIL) {
      toast.error('Cannot delete the main administrator account');
      return;
    }
    
    if (window.confirm('Are you sure you want to delete this user?')) {
      try {
        await api.delete(`/users/${id}`);
        toast.success('User deleted');
        fetchUsers();
      } catch (error) {
        toast.error(error.response?.data?.detail || 'Failed to delete user');
      }
    }
  };

  const resetForm = () => {
    setEditingUser(null);
    setFormData({ full_name: '', email: '', password: '', role: 'Driver', session_timeout_seconds: 120, allowed_modules: null, module_permissions: {} });
  };

  const toggleModule = (moduleKey) => {
    setFormData(prev => {
      const current = Array.isArray(prev.allowed_modules) ? prev.allowed_modules : [];
      const nextModules = current.includes(moduleKey)
        ? current.filter(k => k !== moduleKey)
        : [...current, moduleKey];
      // Drop permission for removed modules; default new modules to 'edit'
      const nextPerms = { ...(prev.module_permissions || {}) };
      if (!nextModules.includes(moduleKey)) {
        delete nextPerms[moduleKey];
      } else if (!nextPerms[moduleKey]) {
        nextPerms[moduleKey] = 'edit';
      }
      return { ...prev, allowed_modules: nextModules, module_permissions: nextPerms };
    });
  };

  const toggleModulePermission = (moduleKey) => {
    setFormData(prev => {
      const current = prev.module_permissions?.[moduleKey] || 'edit';
      return {
        ...prev,
        module_permissions: {
          ...(prev.module_permissions || {}),
          [moduleKey]: current === 'edit' ? 'view' : 'edit',
        },
      };
    });
  };

  const applyRoleDefaultModules = () => {
    const defaults = roleDefaults[formData.role] || [];
    // All role defaults start with 'edit' permission
    const defaultPerms = {};
    defaults.forEach(k => { defaultPerms[k] = 'edit'; });
    setFormData(prev => ({ ...prev, allowed_modules: [...defaults], module_permissions: defaultPerms }));
  };

  const formatLastActivity = (dateStr) => {
    if (!dateStr) return 'N/A';
    const date = new Date(dateStr);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    
    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins} min ago`;
    if (diffMins < 1440) return `${Math.floor(diffMins / 60)} hours ago`;
    return date.toLocaleDateString('en-GB');
  };

  if (loading) {
    return (
      <Layout user={user} onLogout={onLogout}>
        <div className="text-center py-12">Loading...</div>
      </Layout>
    );
  }

  return (
    <Layout user={user} onLogout={onLogout}>
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-4xl font-bold text-slate-900">User Management</h1>
            <p className="text-slate-600 mt-2">Manage admin, supervisor, and driver accounts</p>
          </div>
          <Dialog open={dialogOpen} onOpenChange={(open) => { setDialogOpen(open); if (!open) resetForm(); }}>
            <DialogTrigger asChild>
              <Button data-testid="add-user-button">
                <Plus className="h-4 w-4 mr-2" />
                Add User
              </Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>{editingUser ? 'Edit User' : 'Add New User'}</DialogTitle>
                <DialogDescription>
                  {editingUser 
                    ? 'Update user details below. Leave password empty to keep existing password.' 
                    : 'Create a new user account'}
                </DialogDescription>
              </DialogHeader>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="full_name">Full Name</Label>
                  <Input
                    id="full_name"
                    value={formData.full_name}
                    onChange={(e) => setFormData({ ...formData, full_name: e.target.value })}
                    placeholder="John Doe"
                    required
                    data-testid="user-name-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="email">Email</Label>
                  <Input
                    id="email"
                    type="email"
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    placeholder="john@company.com"
                    required
                    data-testid="user-email-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="password">Password {editingUser && '(leave empty to keep current)'}</Label>
                  <Input
                    id="password"
                    type="password"
                    value={formData.password}
                    onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                    placeholder="••••••••"
                    required={!editingUser}
                    data-testid="user-password-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="role">Role</Label>
                  <Select value={formData.role} onValueChange={(value) => setFormData({ ...formData, role: value })}>
                    <SelectTrigger data-testid="user-role-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="Admin">Admin</SelectItem>
                      <SelectItem value="Manager">Manager</SelectItem>
                      <SelectItem value="Supervisor">Supervisor</SelectItem>
                      <SelectItem value="Driver">Driver</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                {/* Session Timeout */}
                <div className="space-y-2">
                  <Label htmlFor="timeout">Session Timeout (after inactivity)</Label>
                  <Select
                    value={String(formData.session_timeout_seconds)}
                    onValueChange={(value) => setFormData({ ...formData, session_timeout_seconds: Number(value) })}
                  >
                    <SelectTrigger data-testid="user-timeout-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {TIMEOUT_PRESETS.map(p => (
                        <SelectItem key={p.value} value={String(p.value)}>
                          {p.label}
                        </SelectItem>
                      ))}
                      {/* Also show current value if it's not a preset */}
                      {!TIMEOUT_PRESETS.find(p => p.value === Number(formData.session_timeout_seconds)) && (
                        <SelectItem value={String(formData.session_timeout_seconds)}>
                          Custom: {formData.session_timeout_seconds}s
                        </SelectItem>
                      )}
                    </SelectContent>
                  </Select>
                  <p className="text-xs text-slate-500">User will be logged out after this period of inactivity.</p>
                </div>

                {/* Module Access - Admin/Manager only */}
                {canEditModules && (
                  <div className="space-y-2" data-testid="user-modules-section">
                    <div className="flex items-center justify-between">
                      <Label>Module Access (pages this user can open)</Label>
                      <button
                        type="button"
                        onClick={applyRoleDefaultModules}
                        className="text-xs text-blue-600 hover:text-blue-800 underline"
                        data-testid="apply-role-defaults-btn"
                      >
                        Use {formData.role} defaults
                      </button>
                    </div>

                    <div className="p-3 rounded-md border bg-slate-50/60 max-h-[340px] overflow-y-auto space-y-4">
                      {['Main', 'RouteOps'].map((category) => {
                        const mods = availableModules.filter(m => (m.category || 'Main') === category);
                        if (mods.length === 0) return null;
                        return (
                          <div key={category} data-testid={`module-group-${category.toLowerCase()}`}>
                            <div className="text-[11px] font-bold uppercase tracking-wider text-slate-500 mb-2">
                              {category === 'Main' ? 'Main Pages' : 'RouteOps Pages'}
                            </div>
                            <div className="space-y-1.5">
                              {mods.map(m => {
                                const checked = Array.isArray(formData.allowed_modules) && formData.allowed_modules.includes(m.key);
                                const perm = formData.module_permissions?.[m.key] || 'edit';
                                const isEdit = perm === 'edit';
                                return (
                                  <div
                                    key={m.key}
                                    className="flex items-center justify-between gap-2 text-sm py-1 px-2 rounded hover:bg-white/80"
                                    data-testid={`module-row-${m.key}`}
                                  >
                                    <label className="flex items-center gap-2 cursor-pointer flex-1 min-w-0">
                                      <Checkbox
                                        checked={checked}
                                        onCheckedChange={() => toggleModule(m.key)}
                                        data-testid={`module-checkbox-${m.key}`}
                                      />
                                      <span className="truncate">{m.label}</span>
                                    </label>

                                    {/* View / Edit toggle — only active when module is checked */}
                                    <button
                                      type="button"
                                      disabled={!checked}
                                      onClick={() => toggleModulePermission(m.key)}
                                      data-testid={`module-permission-${m.key}`}
                                      className={`text-[10px] font-bold uppercase px-2 py-1 rounded border transition-colors whitespace-nowrap ${
                                        !checked
                                          ? 'opacity-40 border-slate-200 bg-slate-100 text-slate-400 cursor-not-allowed'
                                          : isEdit
                                          ? 'border-emerald-300 bg-emerald-50 text-emerald-700 hover:bg-emerald-100'
                                          : 'border-amber-300 bg-amber-50 text-amber-700 hover:bg-amber-100'
                                      }`}
                                      title={isEdit ? 'Full access — click to switch to view-only' : 'View-only — click to allow editing'}
                                    >
                                      {isEdit ? 'View & Edit' : 'View only'}
                                    </button>
                                  </div>
                                );
                              })}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                    <p className="text-xs text-slate-500">
                      Only Admins/Managers can change module access. Toggle the right-hand button to switch between View-only and full View & Edit.
                    </p>
                  </div>
                )}
                <div className="flex gap-2 pt-4">
                  <Button type="submit" className="flex-1" data-testid="user-submit-button">
                    {editingUser ? 'Update User' : 'Create User'}
                  </Button>
                  <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>Cancel</Button>
                </div>
              </form>
            </DialogContent>
          </Dialog>
        </div>

        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Users className="h-5 w-5" />
              All Users ({users.length})
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              {users.length === 0 ? (
                <div className="text-center py-8 text-slate-500">No users found</div>
              ) : (
                users.map((u) => {
                  const isProtected = u.email === MAIN_ADMIN_EMAIL;
                  const canEdit = isMainAdmin || !isProtected;
                  const canDelete = !isProtected && u.id !== user.id;
                  const isExpanded = expandedUser === u.id;
                  const sessions = userSessions[u.id] || [];
                  const isLoadingSessions = loadingSessions[u.id];
                  
                  return (
                    <div key={u.id} className="border rounded-lg overflow-hidden">
                      {/* User Row */}
                      <div className={`flex items-center justify-between p-4 hover:bg-slate-50 transition-colors ${
                        isProtected ? 'bg-amber-50/50' : ''
                      }`}>
                        <div className="flex items-center gap-4 flex-1">
                          {/* Protected badge */}
                          {isProtected && (
                            <div className="flex items-center justify-center w-8 h-8 rounded-full bg-amber-100">
                              <Shield className="h-4 w-4 text-amber-600" />
                            </div>
                          )}
                          
                          <div className="flex-1">
                            <div className="flex items-center gap-2">
                              <span className="font-medium text-slate-900">{u.full_name}</span>
                              {isProtected && (
                                <span className="text-xs px-2 py-0.5 bg-amber-100 text-amber-700 rounded-full">
                                  Main Admin
                                </span>
                              )}
                            </div>
                            <p className="text-sm text-slate-500">{u.email}</p>
                          </div>
                          
                          <div className="hidden sm:block">
                            <span className={`inline-flex px-2 py-1 text-xs font-medium rounded-full ${
                              u.role === 'Admin' ? 'bg-purple-100 text-purple-700' :
                              u.role === 'Manager' ? 'bg-indigo-100 text-indigo-700' :
                              u.role === 'Supervisor' ? 'bg-blue-100 text-blue-700' :
                              'bg-green-100 text-green-700'
                            }`}>
                              {u.role}
                            </span>
                          </div>
                          
                          <div className="hidden md:block text-sm text-slate-500">
                            {new Date(u.created_at).toLocaleDateString('en-GB')}
                          </div>
                        </div>
                        
                        <div className="flex items-center gap-2">
                          {/* Device sessions toggle - Main Admin only */}
                          {isMainAdmin && (
                            <Button
                              type="button"
                              variant="ghost"
                              size="sm"
                              onClick={() => toggleUserExpansion(u.id)}
                              className="text-slate-500 hover:text-slate-700"
                              title="View active sessions"
                            >
                              <Monitor className="h-4 w-4 mr-1" />
                              {isExpanded ? <ChevronUp className="h-3 w-3" /> : <ChevronDown className="h-3 w-3" />}
                            </Button>
                          )}
                          
                          {canEdit && (
                            <Button 
                              type="button"
                              variant="ghost" 
                              size="sm" 
                              onClick={(e) => {
                                e.preventDefault();
                                e.stopPropagation();
                                handleEdit(u);
                              }} 
                              data-testid={`edit-user-${u.id}`}
                            >
                              <Edit className="h-4 w-4" />
                            </Button>
                          )}
                          
                          {!canEdit && !isMainAdmin && (
                            <Button 
                              type="button"
                              variant="ghost" 
                              size="sm" 
                              disabled
                              className="text-slate-300"
                              title="Protected account"
                            >
                              <Lock className="h-4 w-4" />
                            </Button>
                          )}
                          
                          {canDelete && (
                            <Button 
                              type="button"
                              variant="ghost" 
                              size="sm" 
                              onClick={(e) => {
                                e.preventDefault();
                                e.stopPropagation();
                                handleDelete(u.id, u.email);
                              }} 
                              data-testid={`delete-user-${u.id}`}
                            >
                              <Trash2 className="h-4 w-4 text-red-500" />
                            </Button>
                          )}
                        </div>
                      </div>
                      
                      {/* Sessions Panel - Expanded */}
                      {isMainAdmin && isExpanded && (
                        <div className="border-t bg-slate-50 p-4">
                          <h4 className="text-sm font-medium text-slate-700 mb-3 flex items-center gap-2">
                            <Monitor className="h-4 w-4" />
                            Active Sessions
                          </h4>
                          
                          {isLoadingSessions ? (
                            <div className="text-sm text-slate-500 py-2">Loading sessions...</div>
                          ) : sessions.length === 0 ? (
                            <div className="text-sm text-slate-500 py-2">No active sessions</div>
                          ) : (
                            <div className="space-y-2">
                              {sessions.map((session) => (
                                <div 
                                  key={session.session_id}
                                  className="flex items-center justify-between p-3 bg-white rounded-lg border border-slate-200"
                                >
                                  <div className="flex items-center gap-3">
                                    <DeviceIcon type={session.device_type} />
                                    <div>
                                      <p className="font-medium text-sm text-slate-900">
                                        {session.device_name}
                                      </p>
                                      <p className="text-xs text-slate-500">
                                        Last active: {formatLastActivity(session.last_activity)}
                                      </p>
                                    </div>
                                  </div>
                                  
                                  <Button
                                    type="button"
                                    variant="ghost"
                                    size="sm"
                                    onClick={() => handleForceLogout(session.session_id, session.device_name)}
                                    className="text-red-500 hover:text-red-700 hover:bg-red-50"
                                    title="Force logout this device"
                                  >
                                    <LogOut className="h-4 w-4" />
                                  </Button>
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  );
                })
              )}
            </div>
          </CardContent>
        </Card>
      </div>
    </Layout>
  );
}
